// src/services/pushNotificationService.js
// Push notification utilities and subscription management
import { BASE_URL } from './api';

// Append /api only if it's not already there
const API_BASE = BASE_URL.endsWith('/api')
  ? BASE_URL
  : `${BASE_URL}/api`;

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Convert VAPID public key from base64 to Uint8Array
 */
export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
};

/**
 * Check if push notifications are supported
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
};

/**
 * Get current permission status
 */
export const getPermissionStatus = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

/**
 * Check if already subscribed
 */
export const checkSubscription = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    // console.error('Error checking subscription:', error);
    return false;
  }
};

/**
 * Get current subscription object
 */
export const getCurrentSubscription = async () => {
  if (!isPushSupported()) return null;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    // console.error('Error getting subscription:', error);
    return null;
  }
};

// ============================================
// SUBSCRIPTION MANAGEMENT
// ============================================

/**
 * Request permission and subscribe to push notifications
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  try {
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      throw new Error(`Permission ${permission}`);
    }
    
    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;
    
    // Get VAPID public key from environment
    const vapidPublicKey = 'BIpYhF4btyqGpxoH_8E9uabAT4s_m8DClOs-6kjcH3Qr3rlTxkf73ET11o4brM2ykpbrV36i_K1lk9Xr-mLtt5I';
    
    if (!vapidPublicKey) {
      throw new Error('VAPID public key not configured');
    }
    
    // Subscribe
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    
    // console.log('✅ Push subscription created');
    
    // Send to backend
    await sendSubscriptionToServer(subscription);
    
    return subscription;
    
  } catch (error) {
    // console.error('❌ Push subscription failed:', error);
    throw error;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // console.log('No active subscription');
      return true;
    }
    
    // Unsubscribe from browser
    const result = await subscription.unsubscribe();
    
    if (result) {
      // console.log('✅ Unsubscribed from push');
      
      // Notify backend
      await deleteSubscriptionFromServer(subscription.endpoint);
    }
    
    return result;
    
  } catch (error) {
    // console.error('❌ Unsubscribe failed:', error);
    return false;
  }
};

/**
 * Send subscription to backend
 */
const sendSubscriptionToServer = async (subscription) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetch(`${API_BASE}/push/subscribe.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      subscription: subscription.toJSON()
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save subscription');
  }
  
  return await response.json();
};

/**
 * Delete subscription from backend
 */
const deleteSubscriptionFromServer = async (endpoint) => {
  const token = localStorage.getItem('token');
  
  if (!token) return;
  
  try {
    await fetch(`${API_BASE}/push/unsubscribe.php`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ endpoint })
    });
  } catch (error) {
    // console.error('Failed to delete subscription from server:', error);
  }
};

/**
 * Refresh subscription (called when VAPID key changes or subscription expires)
 */
export const refreshSubscription = async () => {
  await unsubscribeFromPush();
  return await subscribeToPush();
};

// ============================================
// SERVICE WORKER REGISTRATION
// ============================================

/**
 * Register service worker
 */
export const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      // console.log('📱 Service Worker not supported');
      return null;
    }
    
    try {
      // Check if already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      
      if (existingRegistration) {
        // console.log('✅ Service Worker already registered');
        
        // Check if it's active
        if (existingRegistration.active) {
          return existingRegistration;
        }
        
        // Wait for it to become active
        await navigator.serviceWorker.ready;
        return existingRegistration;
      }
      
      // Register new service worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      
      // console.log('✅ Service Worker registered:', registration.scope);
      
      // Wait for activation
      if (registration.installing) {
        // console.log('📦 Service Worker installing...');
        
        await new Promise((resolve) => {
          registration.installing.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') {
              // console.log('✅ Service Worker activated');
              resolve();
            }
          });
        });
      }
      
      // Check for updates periodically
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // console.log('🔄 New service worker available');
            }
          });
        }
      });
      
      return registration;
      
    } catch (error) {
      // Don't throw - push is optional
      // console.warn('⚠️ Service Worker registration failed:', error.message);
      
      // If in development and using HTTP (not HTTPS)
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        // console.warn('💡 Service workers require HTTPS (or localhost). Push notifications will not work.');
      }
      
      return null;
    }
  };

/**
 * Listen for messages from service worker
 */
export const listenForPushMessages = (callback) => {
  if (!('serviceWorker' in navigator)) return () => {};
  
  const handler = (event) => {
    if (event.data && event.data.type === 'PUSH_RECEIVED') {
      // console.log('📨 Push message received from SW:', event.data.payload);
      callback(event.data.payload);
    }
  };
  
  navigator.serviceWorker.addEventListener('message', handler);
  
  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
};

/**
 * Auto-subscribe user if not already subscribed
 * Call this after login/registration
 */
export const autoSubscribeIfNeeded = async () => {
    // Check if push is supported
    if (!isPushSupported()) {
      // console.log('📱 Push notifications not supported in this browser');
      return false;
    }
    
    // Check if already subscribed
    const isSubscribed = await checkSubscription();
    
    if (isSubscribed) {
      // console.log('✅ Already subscribed to push notifications');
      return true;
    }
    
    // Check if permission was previously denied
    if (Notification.permission === 'denied') {
      // console.log('⚠️ Push permission was previously denied, skipping auto-subscribe');
      
      // Store that we attempted (so we don't keep trying)
      localStorage.setItem('push_auto_subscribe_attempted', Date.now().toString());
      
      return false;
    }
    
    // Check if we've attempted recently (don't spam the user)
    const lastAttempt = localStorage.getItem('push_auto_subscribe_attempted');
    if (lastAttempt) {
      const hoursSinceLastAttempt = (Date.now() - parseInt(lastAttempt)) / (1000 * 60 * 60);
      if (hoursSinceLastAttempt < 24) {
        // console.log(`⚠️ Auto-subscribe attempted ${hoursSinceLastAttempt.toFixed(1)} hours ago, skipping`);
        return false;
      }
    }
    
    // If permission is 'default', we can request it
    if (Notification.permission === 'default') {
      // console.log('🔔 Auto-requesting push permission...');
      
      try {
        await subscribeToPush();
        // console.log('✅ Auto-subscribed to push notifications');
        localStorage.removeItem('push_auto_subscribe_attempted');
        return true;
      } catch (error) {
        // console.log('⚠️ Auto-subscribe failed:', error.message);
        localStorage.setItem('push_auto_subscribe_attempted', Date.now().toString());
        return false;
      }
    }
    
    return false;
  };

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  isPushSupported,
  getPermissionStatus,
  checkSubscription,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  refreshSubscription,
  registerServiceWorker,
  listenForPushMessages,
  urlBase64ToUint8Array
};